import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CoupleExercises() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [activeGroup, setActiveGroup] = useState(null);
  const [open, setOpen] = useState(null); // exercise being read in modal

  useEffect(() => {
    axios.get(`${API}/api/exercises`)
      .then(r => {
        const gs = r.data.groups || [];
        setGroups(gs);
        if (gs.length) setActiveGroup(gs[0].id);
      })
      .catch(() => toast.error('Could not load exercises.'));
  }, []);

  const current = groups.find(g => g.id === activeGroup);

  const askVivi = (ex) => {
    try { sessionStorage.setItem('vivi_seed', `Walk me through "${ex.title}" — and how to make it feel natural for us.`); } catch (e) {}
    navigate('/chat');
  };

  return (
    <div style={{ minHeight: '100vh', paddingBottom: 'var(--bottom-h)', position: 'relative' }} data-testid="exercises-page">
      <div className="education-aurora" aria-hidden="true" />

      <div style={{ padding: '18px 16px 8px', position: 'relative', zIndex: 1 }}>
        <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', fontSize: '0.79rem', letterSpacing: '0.15em', padding: 0, marginBottom: 10 }} data-testid="exercises-back">
          <i className="fa-solid fa-arrow-left" /> BACK
        </button>
        <div className="eyebrow">Curated by Dr. Vikram</div>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: '2.35rem', marginBottom: 4 }}>Couple <span className="gold-italic">Exercises</span></div>
        <p style={{ fontFamily: 'var(--font-ui)', fontSize: '1.01rem', color: 'var(--text-muted)', marginBottom: 14, lineHeight: 1.6 }}>
          Small, clinically-grounded rituals to deepen presence, communication and sensual closeness — in minutes a day.
        </p>
      </div>

      {/* Group tabs */}
      <div style={{ padding: '0 16px 12px', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, position: 'relative', zIndex: 1 }} data-testid="exercises-group-tabs">
        {groups.map(g => (
          <button
            key={g.id}
            onClick={() => setActiveGroup(g.id)}
            data-testid={`exercises-group-${g.id}`}
            style={{
              padding: '10px 8px',
              fontSize: '0.76rem',
              letterSpacing: '0.06em',
              fontFamily: 'var(--font-ui)',
              background: activeGroup === g.id ? `${g.color}28` : 'var(--bg2)',
              color: activeGroup === g.id ? g.color : 'var(--text-muted)',
              border: `1px solid ${activeGroup === g.id ? g.color : 'var(--border-soft)'}`,
              cursor: 'pointer',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5,
              minHeight: 60, textAlign: 'center', lineHeight: 1.2,
              transition: 'all 0.18s ease',
            }}
          >
            <i className={`fa-solid ${g.icon}`} style={{ fontSize: '1.1rem' }} />
            {g.label}
          </button>
        ))}
      </div>

      {/* Exercise cards */}
      <div style={{ padding: '0 16px 0', position: 'relative', zIndex: 1, display: 'grid', gap: 10 }}>
        <AnimatePresence mode="wait">
          {current?.items.map((ex, idx) => (
            <motion.button
              key={`${current.id}-${idx}`}
              onClick={() => setOpen(ex)}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.04 }}
              whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }}
              className="immersive-card"
              style={{
                background: 'var(--bg2)', border: `1px solid var(--border-soft)`,
                padding: '14px 16px', textAlign: 'left', color: 'var(--text)', cursor: 'pointer',
                borderLeft: `3px solid ${current.color}`,
              }}
              data-testid={`exercise-${idx}`}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <div style={{ fontSize: '0.68rem', letterSpacing: '0.18em', color: current.color, fontWeight: 700 }}>
                  {ex.duration?.toUpperCase()}
                </div>
                <i className="fa-solid fa-chevron-right" style={{ color: 'var(--text-dim)', fontSize: '0.79rem' }} />
              </div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.32rem', marginBottom: 4 }}>{ex.title}</div>
              <p style={{ fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic', fontSize: '1.04rem', color: 'var(--text-muted)', lineHeight: 1.55 }}>{ex.summary}</p>
            </motion.button>
          ))}
        </AnimatePresence>
      </div>

      {/* Detail modal */}
      <AnimatePresence>
        {open && (
          <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setOpen(null)}>
            <motion.div
              className="modal-sheet"
              initial={{ y: 60 }} animate={{ y: 0 }} exit={{ y: 60 }}
              onClick={ev => ev.stopPropagation()}
              style={{ padding: 22, maxWidth: 460, width: '92%', maxHeight: '85vh', overflowY: 'auto' }}
              data-testid="exercise-modal"
            >
              <div className="eyebrow" style={{ color: current?.color }}>{current?.label} · {open.duration}</div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.85rem', marginBottom: 6 }}>{open.title}</div>
              <p style={{ fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic', color: 'var(--gold)', fontSize: '1.18rem', lineHeight: 1.6, marginBottom: 18 }}>{open.summary}</p>

              <div className="eyebrow" style={{ color: 'var(--text-dim)', fontSize: '0.67rem' }}>HOW TO</div>
              <ol style={{ paddingLeft: 18, margin: '6px 0 18px', color: 'var(--text)' }}>
                {open.steps.map((s, i) => (
                  <li key={i} style={{ fontSize: '1.06rem', lineHeight: 1.7, marginBottom: 8 }}>{s}</li>
                ))}
              </ol>

              <div className="eyebrow" style={{ color: 'var(--text-dim)', fontSize: '0.67rem' }}>WHY IT WORKS</div>
              <p style={{ fontSize: '1.02rem', lineHeight: 1.7, color: 'var(--text-muted)', marginBottom: 18 }}>{open.why}</p>

              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setOpen(null)} className="btn-outline" style={{ flex: 1, padding: '12px', fontSize: '0.79rem' }} data-testid="exercise-close">
                  CLOSE
                </button>
                <button onClick={() => askVivi(open)} className="btn-gold" style={{ flex: 1, padding: '12px', fontSize: '0.79rem' }} data-testid="exercise-ask-vivi">
                  <i className="fa-solid fa-comment-dots" /> ASK VIVI
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
